/**
 * Badge Component
 * 
 * Standardized badge for status indicators, counts, and labels.
 */

import { cn } from '@/utils/cn';
import type { ReactNode } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type BadgeVariant = 
  | 'default' 
  | 'success' 
  | 'warning' 
  | 'error' 
  | 'info'
  | 'primary';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  /** Content to display */
  children: ReactNode;
  /** Visual variant */
  variant?: BadgeVariant;
  /** Size of the badge */
  size?: BadgeSize;
  /** Make the badge rounded (pill shape) */
  pill?: boolean;
  /** Add a dot indicator before the text */
  dot?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// VARIANT STYLES
// ============================================================================

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  error: 'bg-error-subtle text-error',
  info: 'bg-info-subtle text-info',
  primary: 'bg-primary/10 text-primary',
};

const dotStyles: Record<BadgeVariant, string> = {
  default: 'bg-muted-foreground',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
  primary: 'bg-primary',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2.5 py-0.5',
  lg: 'text-sm px-3 py-1',
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Badge - Standardized status/label badge
 * 
 * @example
 * // Simple badge
 * <Badge variant="success">Activo</Badge>
 * 
 * // With dot indicator
 * <Badge variant="warning" dot>Pendiente</Badge>
 * 
 * // Count badge
 * <Badge variant="error" size="sm" pill>5</Badge>
 */
export function Badge({
  children,
  variant = 'default',
  size = 'md',
  pill = true,
  dot = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        // `whitespace-nowrap`: una etiqueta es de una línea por definición.
        // Sin esto, un rol largo —«Agente de Soporte», «Asesor Comercial»—
        // partía en dos dentro de la píldora y rompía su forma en cuanto la
        // columna se estrechaba.
        'inline-flex items-center gap-1.5 whitespace-nowrap font-medium',
        variantStyles[variant],
        sizeStyles[size],
        pill ? 'rounded-full' : 'rounded-md',
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full',
            dotStyles[variant]
          )}
        />
      )}
      {children}
    </span>
  );
}

export default Badge;
